import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardDocumentListIcon, ClipboardDocumentIcon, ArrowPathIcon, ShieldExclamationIcon, ChevronDownIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '../../components/ui/Tooltip';
import api from '../../services/api';

interface InventoryItem {
  name: string;
  version: string;
  category?: string;
}

interface InventoryData {
  generated_at: string;
  dep_hash: string;
  cached_scan: ScanData | null;
  backend_dependencies: InventoryItem[];
  frontend_dependencies: InventoryItem[];
  frontend_dev_dependencies: InventoryItem[];
  runtimes: InventoryItem[];
  databases: InventoryItem[];
  cloud_services: InventoryItem[];
  third_party_integrations: InventoryItem[];
  legacy_cms: InventoryItem[];
  cicd_pipelines: InventoryItem[];
}

interface ScanResult {
  name: string;
  platform: string;
  current_version: string;
  latest_version: string;
  latest_has_issues: boolean;
  vulnerable: boolean;
  vulnerabilities: string[];
}

interface ScanData {
  scan_at: string;
  results: ScanResult[];
  source?: string;
}

function findScan(scanResults: ScanResult[], name: string): ScanResult | undefined {
  return scanResults.find(r => r.name === name || r.name === name.replace(/\[.*\]/, ''));
}

function vulnUrl(id: string): string | null {
  if (id.startsWith('GHSA-')) return `https://github.com/advisories/${id}`;
  if (id.startsWith('CVE-')) return `https://nvd.nist.gov/vuln/detail/${id}`;
  if (id.startsWith('PYSEC-')) return `https://osv.dev/vulnerability/${id}`;
  return null;
}

function parseVulnId(vulnString: string): { id: string; description: string } {
  const colonIdx = vulnString.indexOf(': ');
  if (colonIdx > 0) return { id: vulnString.slice(0, colonIdx), description: vulnString.slice(colonIdx + 2) };
  return { id: vulnString, description: '' };
}

const DepRow: React.FC<{ item: InventoryItem; scan?: ScanResult }> = ({ item, scan }) => {
  const [expanded, setExpanded] = useState(false);
  const isVulnerable = scan?.vulnerable;
  const vulnCount = isVulnerable ? scan.vulnerabilities.length : 0;

  return (
    <div className={isVulnerable ? 'bg-red-50 dark:bg-red-900/20' : ''}>
      <div
        className={`px-4 py-2 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-750 ${isVulnerable ? 'cursor-pointer' : ''}`}
        onClick={isVulnerable ? () => setExpanded(!expanded) : undefined}
      >
        <span className={`text-sm flex items-center gap-1 ${isVulnerable ? 'text-red-700 dark:text-red-400 font-medium' : 'text-label'}`}>
          {isVulnerable && (
            expanded
              ? <ChevronDownIcon className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
              : <ChevronRightIcon className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
          )}
          {item.name}
          {isVulnerable && (
            <Tooltip content={`${vulnCount} known ${vulnCount === 1 ? 'vulnerability' : 'vulnerabilities'} — click to expand`} placement="auto">
              <ShieldExclamationIcon className="inline h-4 w-4 text-red-500" />
            </Tooltip>
          )}
        </span>
        <span className="text-sm flex items-center gap-2">
          <span className={isVulnerable ? 'text-red-700 dark:text-red-400' : 'text-body'}>
            {item.version}
          </span>
          {scan && scan.latest_version && scan.latest_version !== item.version && (
            <span className={scan.latest_has_issues
              ? 'text-red-500 italic'
              : 'text-emerald-600 dark:text-emerald-400'
            }>
              ({scan.latest_version})
            </span>
          )}
        </span>
      </div>
      {expanded && isVulnerable && scan.vulnerabilities.length > 0 && (
        <div className="px-4 pb-2 pl-9 space-y-1">
          {scan.vulnerabilities.map((v) => {
            const { id, description } = parseVulnId(v);
            const url = vulnUrl(id);
            return (
              <div key={id} className="flex items-start gap-2 text-xs">
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 hover:underline dark:bg-red-900/40 dark:text-red-400 dark:hover:bg-red-900/60 transition-colors flex-shrink-0"
                  >
                    {id}
                  </a>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 flex-shrink-0">
                    {id}
                  </span>
                )}
                {description && (
                  <span className="text-gray-600 dark:text-gray-400 pt-0.5">{description}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SectionCard: React.FC<{ title: string; items: InventoryItem[]; showCategory?: boolean; scanResults?: ScanResult[] }> = ({ title, items, showCategory, scanResults }) => {
  const grouped = showCategory
    ? items.reduce<Record<string, InventoryItem[]>>((acc, item) => {
        const cat = item.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
      }, {})
    : null;

  const vulnCount = scanResults
    ? items.filter(item => findScan(scanResults, item.name)?.vulnerable).length
    : 0;

  return (
    <div className="bg-panel shadow rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-rule flex items-center justify-between">
        <h2 className="text-sm font-semibold text-heading uppercase tracking-wide">{title}</h2>
        {vulnCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
            <ShieldExclamationIcon className="h-3.5 w-3.5" />
            {vulnCount}
          </span>
        )}
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {grouped ? (
          Object.entries(grouped).map(([cat, catItems]) => (
            <React.Fragment key={cat}>
              <div className="px-4 py-1.5 bg-gray-25 dark:bg-gray-750">
                <span className="text-xs font-medium text-muted uppercase">{cat}</span>
              </div>
              {catItems.map((item) => (
                <DepRow key={item.name} item={item} scan={scanResults ? findScan(scanResults, item.name) : undefined} />
              ))}
            </React.Fragment>
          ))
        ) : (
          items.map((item) => (
            <DepRow key={item.name} item={item} scan={scanResults ? findScan(scanResults, item.name) : undefined} />
          ))
        )}
        {items.length === 0 && (
          <div className="px-4 py-3 text-sm text-gray-400 italic">No items</div>
        )}
      </div>
    </div>
  );
};

function buildVulnMarkdown(data: InventoryData, scan: ScanData): string {
  const sections: { title: string; items: InventoryItem[] }[] = [
    { title: 'Runtimes & Frameworks', items: data.runtimes },
    { title: 'Databases & Data Access', items: data.databases },
    { title: 'Cloud Services (Azure)', items: data.cloud_services },
    { title: 'Backend Dependencies', items: data.backend_dependencies },
    { title: 'Frontend Dependencies', items: data.frontend_dependencies },
    { title: 'Frontend Dev Dependencies', items: data.frontend_dev_dependencies },
    { title: 'Third-Party API Integrations', items: data.third_party_integrations },
    { title: 'Legacy CMS (V1 - Perl/CGI)', items: data.legacy_cms },
    { title: 'CI/CD Pipeline Matrix', items: data.cicd_pipelines },
  ];

  const totalVuln = scan.results.filter(r => r.vulnerable).length;
  const scanDate = new Date(scan.scan_at + 'Z').toLocaleString();

  const lines: string[] = [
    `# Software Inventory — Vulnerable Dependencies (${totalVuln})`,
    `_Scanned ${scanDate}_`,
    '',
  ];

  for (const section of sections) {
    const vulnItems = section.items
      .map(item => ({ item, result: findScan(scan.results, item.name) }))
      .filter((x): x is { item: InventoryItem; result: ScanResult } => !!x.result?.vulnerable);

    if (vulnItems.length === 0) continue;

    lines.push(`## ${section.title}`);
    for (const { item, result } of vulnItems) {
      const latest = result.latest_version && result.latest_version !== item.version
        ? ` → ${result.latest_version}`
        : '';
      lines.push(`- **${item.name}** ${item.version}${latest}`);
      for (const v of result.vulnerabilities) {
        const { id, description } = parseVulnId(v);
        const url = vulnUrl(id);
        const idText = url ? `[${id}](${url})` : id;
        lines.push(`  - ${idText}${description ? `: ${description}` : ''}`);
      }
    }
    lines.push('');
  }

  return lines.join('\n').trimEnd() + '\n';
}

const SoftwareInventory: React.FC = () => {
  const [data, setData] = useState<InventoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanData, setScanData] = useState<ScanData | null>(null);
  const [scanning, setScanning] = useState(false);
  const [copied, setCopied] = useState(false);

  const runSecurityScan = useCallback(async () => {
    try {
      setScanning(true);
      setError(null);
      const response = await api.post('/admin/software-inventory/security-scan');
      setScanData(response.data);
    } catch (err) {
      setError('Security scan failed');
    } finally {
      setScanning(false);
    }
  }, []);

  const loadInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/software-inventory');
      const inventoryData: InventoryData = response.data;
      setData(inventoryData);

      // Use cached scan if available, otherwise auto-trigger
      if (inventoryData.cached_scan) {
        setScanData(inventoryData.cached_scan);
      } else {
        // No cached scan (deps changed or first run) — auto-scan
        runSecurityScan();
      }
    } catch (err) {
      setError('Failed to load software inventory');
    } finally {
      setLoading(false);
    }
  }, [runSecurityScan]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const vulnTotal = scanData
    ? scanData.results.filter(r => r.vulnerable).length
    : 0;

  const handleCopyVulnerabilities = useCallback(async () => {
    if (!data || !scanData) return;
    try {
      await navigator.clipboard.writeText(buildVulnMarkdown(data, scanData));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  }, [data, scanData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ClipboardDocumentListIcon className="h-6 w-6 text-gray-500" />
          <div>
            <h1 className="text-xl font-semibold text-heading">Software Inventory</h1>
            <div className="flex items-center gap-3 mt-0.5">
              {data?.generated_at && (
                <p className="text-xs text-gray-400">
                  Generated {new Date(data.generated_at + 'Z').toLocaleString()}
                </p>
              )}
              {scanData?.scan_at && (
                <p className="text-xs text-gray-400">
                  | Last scanned {new Date(scanData.scan_at + 'Z').toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyVulnerabilities}
            disabled={!scanData || vulnTotal === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title={vulnTotal === 0 ? 'No vulnerabilities to copy' : `Copy ${vulnTotal} vulnerable ${vulnTotal === 1 ? 'dependency' : 'dependencies'} as markdown`}
          >
            {copied ? (
              <>
                <CheckIcon className="h-4 w-4 text-emerald-500" />
                Copied!
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="h-4 w-4" />
                Copy Vulnerabilities
              </>
            )}
          </button>
          <button
            onClick={runSecurityScan}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-indigo-600 border border-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {scanning ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Scanning...
              </>
            ) : (
              <>
                <ShieldExclamationIcon className="h-4 w-4" />
                Security Scan
              </>
            )}
          </button>
          <button
            onClick={loadInventory}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {scanning && !scanData && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
          Running initial security scan... this may take a moment.
        </div>
      )}

      {scanData && (
        <div className={`mb-4 p-3 rounded-md text-sm flex items-center justify-between ${vulnTotal > 0 ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
          <span>
            {vulnTotal > 0
              ? `Security scan found ${vulnTotal} vulnerable ${vulnTotal === 1 ? 'dependency' : 'dependencies'}.`
              : 'No known vulnerabilities found.'
            }
            {' '}Scanned {scanData.results.length} dependencies.
          </span>
          {scanData.source && (
            <span className="text-xs text-gray-400">
              Source: {scanData.source}
            </span>
          )}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <SectionCard title="Runtimes & Frameworks" items={data.runtimes} scanResults={scanData?.results} />
          <SectionCard title="Databases & Data Access" items={data.databases} scanResults={scanData?.results} />
          <SectionCard title="Cloud Services (Azure)" items={data.cloud_services} scanResults={scanData?.results} />
          <SectionCard title="Backend Dependencies" items={data.backend_dependencies} showCategory scanResults={scanData?.results} />
          <SectionCard title="Frontend Dependencies" items={data.frontend_dependencies} scanResults={scanData?.results} />
          <SectionCard title="Frontend Dev Dependencies" items={data.frontend_dev_dependencies} scanResults={scanData?.results} />
          <SectionCard title="Third-Party API Integrations" items={data.third_party_integrations} scanResults={scanData?.results} />
          <SectionCard title="Legacy CMS (V1 - Perl/CGI)" items={data.legacy_cms} scanResults={scanData?.results} />
          <SectionCard title="CI/CD Pipeline Matrix" items={data.cicd_pipelines} scanResults={scanData?.results} />
        </div>
      )}
    </div>
  );
};

export default SoftwareInventory;
